import React, { useState } from 'react';
import './LocationTree.css';

const LocationTreeNode = ({ 
  node, 
  locationTypes, 
  onAdd, 
  onEdit, 
  onDelete, 
  onManageTypes,
  onAttachWasher,
  washersMap,
  selectionMode,
  onLocationClick,
  onOpenConveyorDetail,
  highlightAttachable,
  parentPath = '',
  isRoot = false
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  
  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };
  
  const hasChildren = node.children && node.children.length > 0;
  const typeName = node.location_node_type?.type_name_ja || '種別不明';
  const hasRfid = node.location_node_type?.has_rfid || false;
  const nodeWashers = washersMap?.[node.location_node_id] || [];
  
  const handleNodeClick = () => {
    if (selectionMode && onLocationClick) {
      onLocationClick(node);
    }
  };
  
  const nodeClass = isRoot 
    ? 'location-node-content location-node-root'
    : (selectionMode ? 'location-node-content selectable' : 'location-node-content');
  
  return (
    <div className="location-tree-node">
      <div className={nodeClass} onClick={selectionMode ? handleNodeClick : undefined}>
        {hasChildren && (
          <button className="location-toggle" onClick={(e) => {
            e.stopPropagation();
            toggleExpand();
          }}>
            {isExpanded ? '▼' : '▶'}
          </button>
        )}
        {!hasChildren && <div className="location-toggle-spacer"></div>}
        
        <div className="location-node-info">
          {!selectionMode && !isRoot && hasRfid && onOpenConveyorDetail ? (
            <button
              type="button"
              className="location-node-name-link"
              onClick={(e) => {
                e.stopPropagation();
                onOpenConveyorDetail(node);
              }}
              title="コンベア詳細を表示"
            >
              {node.location_name || node.name}
            </button>
          ) : (
            <span className="location-node-name">{node.location_name || node.name}</span>
          )}
          {node.location_code && (
            <span className="location-node-code">({node.location_code})</span>
          )}
          {!isRoot && <span className="location-node-type">[{typeName}]</span>}
          {!isRoot && hasRfid && (
            <span className="location-node-badge">RFID有</span>
          )}
          {!isRoot && node.rfid_tag_id && (
            <span className="location-node-rfid">{node.rfid_tag_id}</span>
          )}
          {!isRoot && !node.is_active && (
            <span className="location-node-inactive">無効</span>
          )}
        </div>
        
        {/* 設置されている洗浄機を表示 */}
        {!selectionMode && !isRoot && nodeWashers.length > 0 && (
          <div className="location-washers">
            {nodeWashers.map((washer) => (
              <span key={washer.washer_id} className="washer-tag" title={washer.washer_name}>
                🔧 {washer.washer_name}
              </span>
            ))}
          </div>
        )}
        
        {!selectionMode && (
          <div className="location-node-actions">
            {!isRoot && onAttachWasher && (
              <button 
                className="btn btn-sm btn-secondary location-action-btn" 
                onClick={(e) => {
                  e.stopPropagation();
                  onAttachWasher(node);
                }} 
                title="洗浄機の設置・解除"
              >
                🔧 洗浄機
              </button>
            )}
            <button 
              className="btn btn-sm btn-primary location-action-btn" 
              onClick={(e) => {
                e.stopPropagation();
                onAdd(node);
              }} 
              title={isRoot ? "ルート設置先を追加" : hasRfid ? "最下層（RFID設定可能）のため追加不可" : "設置先を追加"}
              disabled={!isRoot && hasRfid}
              style={{
                cursor: (!isRoot && hasRfid) ? 'not-allowed' : 'pointer',
                opacity: (!isRoot && hasRfid) ? 0.5 : 1
              }}
            >
              + 追加
            </button>
            {!isRoot && (
              <>
                <button 
                  className="btn btn-sm btn-secondary location-action-btn" 
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(node);
                  }} 
                  title="設置先を編集"
                >
                  編集
                </button>
                <button 
                  className="btn btn-sm btn-danger location-action-btn" 
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(node);
                  }} 
                  title="設置先を削除"
                >
                  削除
                </button>
              </>
            )}
          </div>
        )}
      </div>
      
      {hasChildren && isExpanded && (
        <div className="location-children">
          {node.children.map((child, index) => {
            const childPath = parentPath ? `${parentPath}-${child.location_node_id}` : `${node.location_node_id}-${child.location_node_id}`;
            return (
              <LocationTreeNode
                key={childPath}
                node={child}
                locationTypes={locationTypes}
                onAdd={onAdd}
                onEdit={onEdit}
                onDelete={onDelete}
                onManageTypes={onManageTypes}
                onAttachWasher={onAttachWasher}
                washersMap={washersMap}
                selectionMode={selectionMode}
                onLocationClick={onLocationClick}
                onOpenConveyorDetail={onOpenConveyorDetail}
                highlightAttachable={highlightAttachable}
                parentPath={childPath}
                isRoot={false}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

const LocationTree = ({ 
  company, 
  locationNodes, 
  locationTypes, 
  onAdd, 
  onEdit, 
  onDelete, 
  onManageTypes,
  onAttachWasher,
  washersMap,
  selectionMode,
  onLocationClick,
  onOpenConveyorDetail,
  highlightAttachable
}) => {
  if (!company) {
    return (
      <div className="location-tree-empty">
        会社を選択してください
      </div>
    );
  }
  
  // ルートノードを作成（会社名）
  const rootNode = {
    location_node_id: null,
    name: company.company_name,
    children: locationNodes
  };

  return (
    <div className="location-tree">
      {!selectionMode && (
        <div className="location-tree-actions" style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <button className="btn btn-secondary" onClick={onManageTypes}>
            設置先種別マスタ管理
          </button>
        </div>
      )}

      <LocationTreeNode
        node={rootNode}
        locationTypes={locationTypes}
        onAdd={onAdd}
        onEdit={onEdit}
        onDelete={onDelete}
        onManageTypes={onManageTypes}
        onAttachWasher={onAttachWasher}
        washersMap={washersMap}
        selectionMode={selectionMode}
        onLocationClick={onLocationClick}
        onOpenConveyorDetail={onOpenConveyorDetail}
        highlightAttachable={highlightAttachable}
        isRoot={true}
      />

      {locationNodes.length === 0 && (
        <div className="location-tree-empty" style={{ marginTop: '12px' }}>
          設置先が登録されていません。{!selectionMode && '会社名ノードの「+ 追加」から登録してください。'}
        </div>
      )}
    </div>
  );
};

export default LocationTree;
